import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';

import { api, unwrap } from '../api/client';
import { Icon } from '../components/ui/Icon';

import { visibleNav } from './nav';

/** RF-INV-06: cuántos artículos están debajo del mínimo en algún depósito (badge del rail). */
function useBelowMin(enabled: boolean) {
  return useQuery({
    queryKey: ['inventory-items', 'below-min-count'],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const page = await unwrap(
        api.GET('/inventory-items', { params: { query: { belowMin: true, limit: 500 } } }),
      );
      return typeof page === 'string' ? 0 : page.data.length;
    },
  });
}

/** FB-04: ajustes pedidos que esperan la aprobación de Full admin o Gerencia (badge de Pagos). */
function usePendingAdjustments(enabled: boolean) {
  return useQuery({
    queryKey: ['adjustment-requests', 'pending'],
    enabled,
    staleTime: 60_000,
    queryFn: () => unwrap(api.GET('/adjustment-requests', { params: { query: { status: 'pending' } } })),
  });
}

export function Rail({ permissions, logoUrl }: { permissions: readonly string[]; logoUrl: string | null }) {
  const { t } = useTranslation();
  const belowMin = useBelowMin(permissions.includes('inventory.view')).data ?? 0;
  const pendingAdjustments =
    usePendingAdjustments(permissions.includes('ledger.adjust.approve')).data?.length ?? 0;
  return (
    <nav className="rail" aria-label={t('nav.mainMenu')}>
      {/* FB-28: el logo de la clínica si lo subió (Ajustes › Apariencia); si no, la marca. */}
      <div className={logoUrl === null ? 'logo' : 'logo has-img'} aria-hidden="true">
        {logoUrl === null ? 'm' : <img src={logoUrl} alt="" />}
      </div>
      {visibleNav(permissions).map((item) => (
        <NavLink key={item.key} to={item.to}>
          <Icon name={item.icon} />
          {t(`nav.${item.key}`)}
          {item.key === 'payments' && pendingAdjustments > 0 && (
            <>
              <span className="rail-badge" aria-hidden="true">
                {pendingAdjustments}
              </span>
              <span className="sr-only">{t('billing.adjustments.badge', { n: pendingAdjustments })}</span>
            </>
          )}
          {item.key === 'inventory' && belowMin > 0 && (
            <>
              <span className="rail-badge" aria-hidden="true">
                {belowMin}
              </span>
              <span className="sr-only">{t('inventory.belowMinBadge', { n: belowMin })}</span>
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}
